// ======================================================================
// FILE: src/modules/wms/escalation/pages/EscalationPolicyFormPage.js
// MODULE: Work Management System (WMS)
// FEATURE: Escalation Management
//
// PURPOSE:
//   Create / Edit Escalation Policy
//   Manage Escalation Levels
//   Single Master Screen
//
// ROUTES:
//   /wms/escalation-policies/create
//   /wms/escalation-policies/:id/edit
//
// DEPENDENCIES:
//   wmsEscalationService
//   wmsSLAService
//   EscalationLevelsTab
//
// ARCHITECTURE:
//   ✓ TaskFlow ERP Standard
//   ✓ AdminLTE
//   ✓ Multi Tenant
//   ✓ Multi Location
//   ✓ Audit Enabled
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import wmsEscalationService
from "../../services/wmsEscalationService";

import wmsSLAService
from "../../services/wmsSLAService";

import EscalationLevelsTab
from "../components/EscalationLevelsTab";

export default function EscalationPolicyFormPage() {

  const navigate = useNavigate();

  const { id } = useParams();

  const isEdit = !!id;

  const [saving, setSaving] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [slaPolicies, setSLAPolicies] =
    useState([]);

  const [activeTab, setActiveTab] =
    useState("details");

  const [form, setForm] =
    useState({

      escalation_policy_code: "",

      escalation_policy_name: "",

      sla_policy_id: "",

      description: "",

      remarks: "",
    });

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    loadSLAPolicies();

  }, []);

  useEffect(() => {

    if (!isEdit) return;

    loadPolicy();

  }, [id]);

  async function loadSLAPolicies() {

    try {

      const result =
        await wmsSLAService
          .listSLAPolicies({

            page: 1,
            per_page: 1000,
          });

      setSLAPolicies(
        result.items || []
      );

    } catch (err) {

      console.error(err);
    }
  }

  async function loadPolicy() {

    try {

      setLoading(true);

      const data =
        await wmsEscalationService
          .getEscalationPolicy(id);

      setForm({

        escalation_policy_code:
          data.escalation_policy_code || "",

        escalation_policy_name:
          data.escalation_policy_name || "",

        sla_policy_id:
          data.sla_policy_id || "",

        description:
          data.description || "",

        remarks:
          data.remarks || "",
      });

    } catch (err) {

      alert(
        err?.response?.data?.message
        ||
        "Failed to load Escalation Policy"
      );

    } finally {

      setLoading(false);
    }
  }

  // ==========================================================
  // CHANGE
  // ==========================================================

  function handleChange(
    field,
    value
  ) {

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  // ==========================================================
  // VALIDATION
  // ==========================================================

  function validate() {

    if (
      !form.escalation_policy_code?.trim()
    ) {

      alert(
        "Escalation Policy Code is required"
      );

      return false;
    }

    if (
      !form.escalation_policy_name?.trim()
    ) {

      alert(
        "Escalation Policy Name is required"
      );

      return false;
    }

    if (
      !form.sla_policy_id
    ) {

      alert(
        "SLA Policy is required"
      );

      return false;
    }

    return true;
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!validate()) {

      return;
    }

    try {

      setSaving(true);

      const payload = {

        escalation_policy_code:
          form.escalation_policy_code,

        escalation_policy_name:
          form.escalation_policy_name,

        sla_policy_id:
          form.sla_policy_id,

        description:
          form.description,

        remarks:
          form.remarks,
      };

      if (isEdit) {

        await wmsEscalationService
          .updateEscalationPolicy(
            id,
            payload
          );

      } else {

        await wmsEscalationService
          .createEscalationPolicy(
            payload
          );
      }

      navigate(
        "/wms/escalation-policies"
      );

    } catch (err) {

      alert(
        err?.response?.data?.message
        ||
        "Save failed"
      );

    } finally {

      setSaving(false);
    }
  }

  if (loading) {

    return (

      <div className="container-fluid">

        Loading...

      </div>
    );
  }

  return (

    <div className="container-fluid">

      {/* ==================================================== */}
      {/* HEADER */}
      {/* ==================================================== */}

      <div
        className="card border-0 mb-3"
        style={{
          background:
            "linear-gradient(90deg,#0d6efd,#4f8cff)",
          color: "#fff",
        }}
      >

        <div className="card-body">

          <h3 className="mb-1">

            {isEdit
              ? "Edit Escalation Policy"
              : "Create Escalation Policy"}

          </h3>

          <div>

            Configure escalation workflow
            and notifications

          </div>

          <div className="small mt-1">

            Fields marked with

            <span className="text-danger ms-1">
              *
            </span>

            {" "}are mandatory.

          </div>

        </div>

      </div>
            {/* ==================================================== */}
      {/* TABS */}
      {/* ==================================================== */}

      <div className="card">

        <div className="card-header p-0">

          <ul
            className="nav nav-tabs"
            role="tablist"
          >

            <li className="nav-item">

              <button
                type="button"
                className={
                  activeTab === "details"
                    ? "nav-link active"
                    : "nav-link"
                }
                onClick={() =>
                  setActiveTab(
                    "details"
                  )
                }
              >
                Escalation Details
              </button>

            </li>

            <li className="nav-item">

              <button
                type="button"
                className={
                  activeTab === "levels"
                    ? "nav-link active"
                    : "nav-link"
                }
                onClick={() =>
                  setActiveTab(
                    "levels"
                  )
                }
                disabled={!isEdit}
              >
                Escalation Levels
              </button>

            </li>

          </ul>

        </div>

        <div className="card-body">

          {/* ================================================= */}
          {/* DETAILS TAB */}
          {/* ================================================= */}

          {activeTab === "details" && (

            <div className="row">

              {/* CODE */}

              <div className="col-md-3 mb-3">

                <label>

                  Escalation Policy Code

                  <span className="text-danger ms-1">
                    *
                  </span>

                </label>

                <input
                  className="form-control"
                  value={
                    form.escalation_policy_code
                  }
                  onChange={(e) =>
                    handleChange(
                      "escalation_policy_code",
                      e.target.value
                    )
                  }
                />

              </div>

              {/* NAME */}

              <div className="col-md-5 mb-3">

                <label>

                  Escalation Policy Name

                  <span className="text-danger ms-1">
                    *
                  </span>

                </label>

                <input
                  className="form-control"
                  value={
                    form.escalation_policy_name
                  }
                  onChange={(e) =>
                    handleChange(
                      "escalation_policy_name",
                      e.target.value
                    )
                  }
                />

              </div>

              {/* SLA POLICY */}

              <div className="col-md-4 mb-3">

                <label>

                  SLA Policy

                  <span className="text-danger ms-1">
                    *
                  </span>

                </label>

                <select
                  className="form-control"
                  value={
                    form.sla_policy_id
                  }
                  onChange={(e) =>
                    handleChange(
                      "sla_policy_id",
                      e.target.value
                    )
                  }
                >

                  <option value="">
                    Select SLA Policy
                  </option>

                  {slaPolicies.map(
                    (policy) => (

                    <option
                      key={policy.id}
                      value={policy.id}
                    >
                      {
                        policy.sla_policy_name
                      }
                    </option>

                    )
                  )}

                </select>

              </div>

              {/* DESCRIPTION */}

              <div className="col-md-12 mb-3">

                <label>
                  Description
                </label>

                <textarea
                  rows="3"
                  className="form-control"
                  value={
                    form.description
                  }
                  onChange={(e) =>
                    handleChange(
                      "description",
                      e.target.value
                    )
                  }
                />

              </div>

              {/* REMARKS */}

              <div className="col-md-12 mb-3">

                <label>
                  Remarks
                </label>

                <textarea
                  rows="3"
                  className="form-control"
                  value={
                    form.remarks
                  }
                  onChange={(e) =>
                    handleChange(
                      "remarks",
                      e.target.value
                    )
                  }
                />

              </div>

            </div>

          )}

          {/* ================================================= */}
          {/* LEVELS TAB */}
          {/* ================================================= */}

          {activeTab === "levels" && (

            <EscalationLevelsTab
              policyId={id}
              isEdit={isEdit}
            />

          )}

        </div>

      </div>

      {/* ==================================================== */}
      {/* FOOTER */}
      {/* ==================================================== */}

      <div className="card mt-3">

        <div className="card-body text-end">

          <button
            className="btn btn-primary me-2"
            onClick={handleSave}
            disabled={saving}
          >

            {saving
              ? "Saving..."
              : "Save"}

          </button>

          <button
            className="btn btn-secondary"
            onClick={() =>
              navigate(
                "/wms/escalation-policies"
              )
            }
          >
            Cancel
          </button>

        </div>

      </div>

    </div>
  );
}